import {
    Fragment,
    useEffect,
    useMemo,
    useState
} from "react";
import { useSelector } from "react-redux";

import {
    PlusIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";

import {
    selectMemberships
} from "../lib/scraper.slice";
import {
    BackendObj
} from "../lib/backend";
import {
    ILookupTableBase,
    IWidgetCountNumberDetails,
    IWidgetDateCountChartDetails,
    IWidgetDateDistributionChartDetails,
    IWidgetDateDistributionChartValuesType,
    IWidgetDateRatioChartDetails,
    IWidgetDateSumChartDetails,
    IWidgetDateTagsChartDetails,
    IWidgetDetails,
    IWidgetFilter,
    IWidgetFunnelChartDetails,
    IWidgetFunnelChartStage,
    IWidgetPivotAggrListDetails,
    IWidgetPivotTagsCountDetails,
    IWidgetSumNumberDetails,
    IWidgetTagsChartDetails,
    WidgetFilterOperator,
    WidgetType
} from "../lib/backend/extractions.types.generated";

import { Button, IconButton } from "../components/Button";
import { IntegerInput, Textbox } from "../components/Textbox";
import { Dropdown } from "../components/Dropdown";
import { SidePanelRaw } from "../components/SidePanel";
import { ButtonMenu } from "./ButtonMenu";
import { OrgPill } from "./OrgPill";
import { Checkbox } from "./Checkbox";

const WIDGET_TYPES: WidgetType[] = [
    "date_count_chart",
    "date_distribution_chart",
    "date_ratio_chart",
    "date_tags_chart",
    "tags_chart",
    "date_sum_chart",
    "sum_number",
    "count_number",
    "pivot_tags_count",
    "pivot_aggr_list",
    "funnel_chart"
];

const EMPTY_FUNNEL_STAGE: IWidgetFunnelChartStage = { lookup_table_uuid: "", filters: [], date_header_uuid: "", name: "", aggregation_type: "count" };

function getWidgetDetails(type: WidgetType): IWidgetDetails {
    switch (type) {
        case "date_count_chart": return { lookup_table_uuid: "", filters: [], date_header_uuid: "" };
        case "date_distribution_chart": return { lookup_table_uuid: "", filters: [], date_header_uuid: "", values_header_uuid: "" };
        case "date_ratio_chart": return { lookup_table_uuid: "", filters: [], date_header_uuid: "", value_header_uuid: "", value: "" };
        case "date_tags_chart": return { lookup_table_uuid: "", filters: [], date_header_uuid: "", tags_headers_uuids: [""], tags_header_names: ["Tag"] };
        case "tags_chart": return { lookup_table_uuid: "", filters: [], date_header_uuid: "", tags_headers_uuids: [""], tags_header_names: ["Tag"] };
        case "date_sum_chart": return { lookup_table_uuid: "", filters: [], date_header_uuid: "", values_headers_uuids: [""], values_header_names: ["Sum"] };
        case "sum_number": return { lookup_table_uuid: "", filters: [], date_header_uuid: "", values_headers_uuids: [""], values_header_names: ["Sum"] };
        case "count_number": return { lookup_table_uuid: "", filters: [], date_header_uuid: "" };
        case "pivot_tags_count": return { lookup_table_uuid: "", filters: [], date_header_uuid: "", pivot_headers_uuids: [], tags_headers_uuids: [""], tags_header_names: ["Tag"], top_n: 5 };
        case "pivot_aggr_list": return { lookup_table_uuid: "", filters: [], date_header_uuid: "", pivot_headers_uuids: [], aggregation_type: "count", value_header_uuid: "", top_n: 5, trend: "none" };
        case "funnel_chart": return { stages: [1, 2, 3].map((_) => EMPTY_FUNNEL_STAGE) };
    }
}

type WidgetFiltersProps = {
    lookup_table: ILookupTableBase;
    filters: IWidgetFilter[];
    updateFilters: (filters: IWidgetFilter[]) => void;
}

function WidgetFilters({ lookup_table, filters, updateFilters }: WidgetFiltersProps) {
    const addWidgetFilter = (type: "fixed" | "dynamic") => {
        updateFilters([...filters, { column_uuid: lookup_table.headers[0].uuid, values: [""], type }]);
    }

    const updateNewWidgetFilterColumnUuid = (idx: number, value: string) => {
        const old_filter = filters[idx];
        if (old_filter === undefined) { return; }
        old_filter.column_uuid = value;
        updateFilters([...filters.slice(0, idx), old_filter, ...filters.slice(idx + 1)]);
    }

    const updateNewWidgetFilterOperator = (idx: number, value: WidgetFilterOperator) => {
        const old_filter = filters[idx];
        if (old_filter === undefined) { return; }
        old_filter.operator = value;
        updateFilters([...filters.slice(0, idx), old_filter, ...filters.slice(idx + 1)]);
    }

    const updateNewWidgetFilterValue = (idx: number, value_idx: number, value: string) => {
        const old_filter = filters[idx];
        if (old_filter === undefined) { return; }
        old_filter.values[value_idx] = value;
        // remove empty values
        old_filter.values = old_filter.values.filter((value) => value.trim() !== "");
        // ensure at least one value
        if (old_filter.values.length === 0) { old_filter.values.push(""); }
        updateFilters([...filters.slice(0, idx), old_filter, ...filters.slice(idx + 1)]);
    }

    const addNewWidgetFilterValue = (idx: number) => {
        const old_filter = filters[idx];
        if (old_filter === undefined) { return; }
        old_filter.values.push("");
        updateFilters([...filters.slice(0, idx), old_filter, ...filters.slice(idx + 1)]);
    }

    const removeNewWidgetFilter = (idx: number) => {
        updateFilters(filters.filter((_, i) => i !== idx));
    }

    return <Fragment>
        {filters.map((filter, filter_idx) => <Fragment key={filter_idx}>
            {filter.type === "fixed" && <Fragment>
                <div className="flex flex-row items-center gap-x-2">
                    <IconButton icon={XMarkIcon} onClick={() => removeNewWidgetFilter(filter_idx)} size="small" />
                    <label htmlFor="title" className="text-sm font-medium leading-6 text-gray-900">
                        Filter {filter_idx + 1}
                    </label>
                </div>
                {filter.values.map((value, value_idx) => <Fragment key={value_idx}>
                    {value_idx === 0 && <div className="col-span-2 grid grid-cols-11 gap-x-2 items-center">
                        <div className="col-span-4">
                            <Dropdown
                                values={lookup_table.headers.map((header) => header.name)}
                                ids={lookup_table.headers.map((header) => header.uuid)}
                                selected={`${filter.column_uuid}`}
                                placeholder="Select a column"
                                onChange={(value) => updateNewWidgetFilterColumnUuid(filter_idx, value)}
                            />
                        </div>
                        <div className="col-span-2">
                            <Dropdown
                                values={["=", "⊆", "⊇", "≠"]}
                                ids={["eq", "text_contains", "text_contained_in", "ne"]}
                                selected={filter.operator ?? "eq"}
                                onChange={(value) => updateNewWidgetFilterOperator(filter_idx, value as WidgetFilterOperator)}
                            />
                        </div>
                        <div className="col-span-4">
                            <Textbox
                                key={value_idx}
                                value={value}
                                onChange={(value) => updateNewWidgetFilterValue(filter_idx, value_idx, value)}
                            />
                        </div>
                        <div className="col-span-1 flex items-center">
                            {value_idx === filter.values.length - 1 && <IconButton icon={PlusIcon} onClick={() => addNewWidgetFilterValue(filter_idx)} />}
                        </div>
                    </div>}
                    {value_idx > 0 && <Fragment>
                        <div />
                        <div className="col-span-2 grid grid-cols-11 gap-x-2 items-center">
                            <div className="col-span-6" />
                            <div className="col-span-4">
                                <Textbox
                                    key={value_idx}
                                    value={value}
                                    onChange={(value) => updateNewWidgetFilterValue(filter_idx, value_idx, value)}
                                />
                            </div>
                            <div className="col-span-1 flex items-center">
                                {value_idx === filter.values.length - 1 && <IconButton icon={PlusIcon} onClick={() => addNewWidgetFilterValue(filter_idx)} />}
                            </div>
                        </div>
                    </Fragment>}
                </Fragment>)}
            </Fragment>}
            {filter.type === "dynamic" && <Fragment>
                <div className="flex flex-row items-center gap-x-2">
                    <IconButton icon={XMarkIcon} onClick={() => removeNewWidgetFilter(filter_idx)} size="small" />
                    <label htmlFor="title" className="text-sm font-medium leading-6 text-gray-900">
                        Filter {filter_idx + 1} (dynamic)
                    </label>
                </div>
                <div className="flex flex-col col-span-2 items-start">
                    <Dropdown
                        values={lookup_table.headers.map((header) => header.name)}
                        ids={lookup_table.headers.map((header) => header.uuid)}
                        selected={`${filter.column_uuid}`}
                        placeholder="Select a column"
                        onChange={(value) => updateNewWidgetFilterColumnUuid(filter_idx, value)}
                    />
                </div>
            </Fragment>}
        </Fragment>)}
        <div className="col-span-3 flex flex-row items-center gap-x-2 justify-end">
            <ButtonMenu
                title="Add Filter"
                items={[
                    { title: "Fixed Values", onClick: () => addWidgetFilter("fixed") },
                    { title: "Dynamic Values", onClick: () => addWidgetFilter("dynamic") }
                ]}
            />
        </div>
    </Fragment>;
}

type WidgetDetailsProps<T> = {
    lookup_tables: ILookupTableBase[];
    details: T;
    updateDetails: (details: T) => void;
}

function WidgetDateCountChartDetails({ lookup_tables, details, updateDetails }: WidgetDetailsProps<IWidgetDateCountChartDetails>) {
    const lookup_table = lookup_tables.find(lookup_table => lookup_table.uuid === details.lookup_table_uuid);

    return <Fragment>
        <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
            Table
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Dropdown
                values={lookup_tables.map((lookup_table) => lookup_table.name)}
                ids={lookup_tables.map((lookup_table) => lookup_table.uuid)}
                selected={`${details.lookup_table_uuid}`}
                placeholder="Select a table"
                onChange={(value) => updateDetails({ ...details, lookup_table_uuid: value })}
            />
        </div>

        {lookup_table !== undefined && <Fragment>
            <WidgetFilters
                lookup_table={lookup_table}
                filters={details.filters}
                updateFilters={(filters) => updateDetails({ ...details, filters })} />

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Date Column
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${details.date_header_uuid}`}
                    placeholder="Select a date column"
                    onChange={(value) => updateDetails({ ...details, date_header_uuid: value })}
                />
            </div>
        </Fragment>}
    </Fragment>
}

function WidgetDateDistributionChartDetails({ lookup_tables, details, updateDetails }: WidgetDetailsProps<IWidgetDateDistributionChartDetails>) {
    const lookup_table = lookup_tables.find(lookup_table => lookup_table.uuid === details.lookup_table_uuid);

    return <Fragment>
        <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
            Table
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Dropdown
                values={lookup_tables.map((lookup_table) => lookup_table.name)}
                ids={lookup_tables.map((lookup_table) => lookup_table.uuid)}
                selected={`${details.lookup_table_uuid}`}
                placeholder="Select a table"
                onChange={(value) => updateDetails({ ...details, lookup_table_uuid: value })}
            />
        </div>

        {lookup_table !== undefined && <Fragment>
            <WidgetFilters
                lookup_table={lookup_table}
                filters={details.filters}
                updateFilters={(filters) => updateDetails({ ...details, filters })} />

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Date Column
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${details.date_header_uuid}`}
                    placeholder="Select a date column"
                    onChange={(value) => updateDetails({ ...details, date_header_uuid: value })}
                />
            </div>
            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Values Column
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${details.values_header_uuid}`}
                    placeholder="Select a values column"
                    onChange={(value) => updateDetails({ ...details, values_header_uuid: value })}
                />
            </div>
            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Values Type
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={["Validation Results", "Yes/No", "All Values"]}
                    ids={["validation", "yes_no", "all_values"]}
                    selected={`${details.type}`}
                    onChange={(id) => updateDetails({ ...details, type: id as IWidgetDateDistributionChartValuesType })}
                />
            </div>
        </Fragment>}
    </Fragment>;
}

function WidgetDateRatioChartDetails({ lookup_tables, details, updateDetails }: WidgetDetailsProps<IWidgetDateRatioChartDetails>) {
    const lookup_table = lookup_tables.find(lookup_table => lookup_table.uuid === details.lookup_table_uuid);

    return <Fragment>
        <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
            Table
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Dropdown
                values={lookup_tables.map((lookup_table) => lookup_table.name)}
                ids={lookup_tables.map((lookup_table) => lookup_table.uuid)}
                selected={`${details.lookup_table_uuid}`}
                placeholder="Select a table"
                onChange={(value) => updateDetails({ ...details, lookup_table_uuid: value })}
            />
        </div>

        {lookup_table !== undefined && <Fragment>
            <WidgetFilters
                lookup_table={lookup_table}
                filters={details.filters}
                updateFilters={(filters) => updateDetails({ ...details, filters })} />

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Date Column
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${details.date_header_uuid}`}
                    placeholder="Select a date column"
                    onChange={(value) => updateDetails({ ...details, date_header_uuid: value })}
                />
            </div>
            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Value Column
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${details.value_header_uuid}`}
                    placeholder="Select a value column"
                    onChange={(value) => updateDetails({ ...details, value_header_uuid: value })}
                />
            </div>
            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Value
            </label>
            <Textbox
                value={`${details.value}`}
                onChange={(value) => updateDetails({ ...details, value: value })}
            />
            <Textbox
                value={`${details.value_display_name ?? ""}`}
                onChange={(value) => updateDetails({ ...details, value_display_name: value !== "" ? value : undefined })}
                placeholder="Optional display name"
            />
        </Fragment>}
    </Fragment>;
}

function WidgetDateTagsChartDetails({ lookup_tables, details, updateDetails }: WidgetDetailsProps<IWidgetDateTagsChartDetails>) {
    const lookup_table = lookup_tables.find(lookup_table => lookup_table.uuid === details.lookup_table_uuid);

    return <Fragment>
        <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
            Table
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Dropdown
                values={lookup_tables.map((lookup_table) => lookup_table.name)}
                ids={lookup_tables.map((lookup_table) => lookup_table.uuid)}
                selected={`${details.lookup_table_uuid}`}
                placeholder="Select a table"
                onChange={(value) => updateDetails({ ...details, lookup_table_uuid: value })}
            />
        </div>

        {lookup_table !== undefined && <Fragment>
            <WidgetFilters
                lookup_table={lookup_table}
                filters={details.filters}
                updateFilters={(filters) => updateDetails({ ...details, filters })} />

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Date Column
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${details.date_header_uuid}`}
                    placeholder="Select a date column"
                    onChange={(value) => updateDetails({ ...details, date_header_uuid: value })}
                />
            </div>
            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Tags Columns
            </label>
            {details.tags_headers_uuids.map((tag_headers_uuid, tag_idx) => <Fragment key={tag_idx}>
                {tag_idx > 0 && <div />}
                <div className="flex flex-row w-full gap-x-2 col-span-2">
                    <Dropdown
                        values={lookup_table.headers.map((header) => header.name)}
                        ids={lookup_table.headers.map((header) => header.uuid)}
                        selected={tag_headers_uuid}
                        placeholder="Select a tags column"
                        onChange={(value) => updateDetails({ ...details, tags_headers_uuids: details.tags_headers_uuids.map((_, idx) => idx === tag_idx ? value : _) })}
                    />
                    <Textbox
                        value={details.tags_header_names[tag_idx]}
                        onChange={(value) => updateDetails({ ...details, tags_header_names: details.tags_header_names.map((_, idx) => idx === tag_idx ? value : _) })}
                    />
                    {details.tags_headers_uuids.length > 1 && <IconButton
                        icon={XMarkIcon}
                        onClick={() => updateDetails({ ...details, tags_headers_uuids: details.tags_headers_uuids.filter((_, idx) => idx !== tag_idx) })}
                    />}
                </div>
            </Fragment>)}
            <div className="col-span-2" />
            <Button icon={PlusIcon} text="Add Tag" onClick={() => {
                updateDetails({
                    ...details,
                    tags_headers_uuids: [...details.tags_headers_uuids, ""],
                    tags_header_names: [...details.tags_header_names, ""]
                });
            }} />
        </Fragment>}
    </Fragment>;
}

function WidgetTagsChartDetails({ lookup_tables, details, updateDetails }: WidgetDetailsProps<IWidgetTagsChartDetails>) {
    const lookup_table = lookup_tables.find(lookup_table => lookup_table.uuid === details.lookup_table_uuid);

    return <Fragment>
        <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
            Table
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Dropdown
                values={lookup_tables.map((lookup_table) => lookup_table.name)}
                ids={lookup_tables.map((lookup_table) => lookup_table.uuid)}
                selected={`${details.lookup_table_uuid}`}
                placeholder="Select a table"
                onChange={(value) => updateDetails({ ...details, lookup_table_uuid: value })}
            />
        </div>

        {lookup_table !== undefined && <Fragment>
            <WidgetFilters
                lookup_table={lookup_table}
                filters={details.filters}
                updateFilters={(filters) => updateDetails({ ...details, filters })} />

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Date Column
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${details.date_header_uuid}`}
                    placeholder="Select a date column"
                    onChange={(value) => updateDetails({ ...details, date_header_uuid: value })}
                />
            </div>
            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Tags Columns
            </label>
            {details.tags_headers_uuids.map((tag_headers_uuid, tag_idx) => <Fragment key={tag_idx}>
                {tag_idx > 0 && <div />}
                <div className="flex flex-row w-full gap-x-2 col-span-2">
                    <Dropdown
                        values={lookup_table.headers.map((header) => header.name)}
                        ids={lookup_table.headers.map((header) => header.uuid)}
                        selected={tag_headers_uuid}
                        placeholder="Select a tags column"
                        onChange={(value) => updateDetails({ ...details, tags_headers_uuids: details.tags_headers_uuids.map((_, idx) => idx === tag_idx ? value : _) })}
                    />
                    <Textbox
                        value={details.tags_header_names[tag_idx]}
                        onChange={(value) => updateDetails({ ...details, tags_header_names: details.tags_header_names.map((_, idx) => idx === tag_idx ? value : _) })}
                    />
                    {details.tags_headers_uuids.length > 1 && <IconButton
                        icon={XMarkIcon}
                        onClick={() => updateDetails({ ...details, tags_headers_uuids: details.tags_headers_uuids.filter((_, idx) => idx !== tag_idx) })}
                    />}
                </div>
            </Fragment>)}
            <div className="col-span-2" />
            <Button icon={PlusIcon} text="Add Tag" onClick={() => {
                updateDetails({
                    ...details,
                    tags_headers_uuids: [...details.tags_headers_uuids, ""],
                    tags_header_names: [...details.tags_header_names, ""]
                });
            }} />
        </Fragment>}
    </Fragment>;
}

function WidgetDateSumChartDetails({ lookup_tables, details, updateDetails }: WidgetDetailsProps<IWidgetDateSumChartDetails>) {
    const lookup_table = lookup_tables.find(lookup_table => lookup_table.uuid === details.lookup_table_uuid);

    return <Fragment>
        <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
            Table
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Dropdown
                values={lookup_tables.map((lookup_table) => lookup_table.name)}
                ids={lookup_tables.map((lookup_table) => lookup_table.uuid)}
                selected={`${details.lookup_table_uuid}`}
                placeholder="Select a table"
                onChange={(value) => updateDetails({ ...details, lookup_table_uuid: value })}
            />
        </div>

        {lookup_table !== undefined && <Fragment>
            <WidgetFilters
                lookup_table={lookup_table}
                filters={details.filters}
                updateFilters={(filters) => updateDetails({ ...details, filters })} />

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Date Column
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${details.date_header_uuid}`}
                    placeholder="Select a date column"
                    onChange={(value) => updateDetails({ ...details, date_header_uuid: value })}
                />
            </div>
            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Values Headers
            </label>
            {details.values_headers_uuids.map((values_headers_uuid, values_idx) => <Fragment key={values_idx}>
                {values_idx > 0 && <div />}
                <div className="flex flex-row w-full gap-x-2 col-span-2">
                    <Dropdown
                        values={lookup_table.headers.map((header) => header.name)}
                        ids={lookup_table.headers.map((header) => header.uuid)}
                        selected={values_headers_uuid}
                        onChange={(value) => updateDetails({ ...details, values_headers_uuids: details.values_headers_uuids.map((_, idx) => idx === values_idx ? value : _) })}
                    />
                    <Textbox
                        value={details.values_header_names[values_idx]}
                        onChange={(value) => updateDetails({ ...details, values_header_names: details.values_header_names.map((_, idx) => idx === values_idx ? value : _) })}
                    />
                    {details.values_headers_uuids.length > 1 && <IconButton
                        icon={XMarkIcon}
                        onClick={() => updateDetails({ ...details, values_headers_uuids: details.values_headers_uuids.filter((_, idx) => idx !== values_idx) })}
                    />}
                </div>
            </Fragment>)}
            <div className="col-span-2" />
            <Button icon={PlusIcon} text="Add Value" onClick={() => {
                updateDetails({
                    ...details,
                    values_headers_uuids: [...details.values_headers_uuids, ""],
                    values_header_names: [...details.values_header_names, ""]
                });
            }} />
        </Fragment>}
    </Fragment>;
}

function WidgetSumNumberDetails({ lookup_tables, details, updateDetails }: WidgetDetailsProps<IWidgetSumNumberDetails>) {
    const lookup_table = lookup_tables.find(lookup_table => lookup_table.uuid === details.lookup_table_uuid);

    return <Fragment>
        <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
            Table
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Dropdown
                values={lookup_tables.map((lookup_table) => lookup_table.name)}
                ids={lookup_tables.map((lookup_table) => lookup_table.uuid)}
                selected={`${details.lookup_table_uuid}`}
                placeholder="Select a table"
                onChange={(value) => updateDetails({ ...details, lookup_table_uuid: value })}
            />
        </div>

        {lookup_table !== undefined && <Fragment>
            <WidgetFilters
                lookup_table={lookup_table}
                filters={details.filters}
                updateFilters={(filters) => updateDetails({ ...details, filters })} />

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Date Column
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${details.date_header_uuid}`}
                    placeholder="Select a date column"
                    onChange={(value) => updateDetails({ ...details, date_header_uuid: value })}
                />
            </div>
            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Value Header
            </label>
            <div className="flex flex-row w-full gap-x-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={details.values_headers_uuid}
                    onChange={(value) => updateDetails({ ...details, values_headers_uuid: value })}
                />
            </div>
            <div className="col-span-2" />
            <Button icon={PlusIcon} text="Add Value" onClick={() => { updateDetails({ ...details, values_headers_uuid: "" }); }} />
        </Fragment>}

        <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
            Max. No. of Decimals
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <IntegerInput
                value={details.decimals ?? 2}
                onChange={(value) => updateDetails({ ...details, decimals: value === 2 ? undefined : value })}
            />
        </div>
        <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
            Prefix
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Textbox
                value={details.prefix ?? ""}
                onChange={(value) => updateDetails({ ...details, prefix: value === "" ? undefined : value })}
            />
        </div>
        <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
            Suffix
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Textbox
                value={details.suffix ?? ""}
                onChange={(value) => updateDetails({ ...details, suffix: value === "" ? undefined : value })}
            />
        </div>
    </Fragment>;
}

function WidgetCountNumberDetails({ lookup_tables, details, updateDetails }: WidgetDetailsProps<IWidgetCountNumberDetails>) {
    const lookup_table = lookup_tables.find(lookup_table => lookup_table.uuid === details.lookup_table_uuid);

    return <Fragment>
        <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
            Table
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Dropdown
                values={lookup_tables.map((lookup_table) => lookup_table.name)}
                ids={lookup_tables.map((lookup_table) => lookup_table.uuid)}
                selected={`${details.lookup_table_uuid}`}
                placeholder="Select a table"
                onChange={(value) => updateDetails({ ...details, lookup_table_uuid: value })}
            />
        </div>

        {lookup_table !== undefined && <Fragment>
            <WidgetFilters
                lookup_table={lookup_table}
                filters={details.filters}
                updateFilters={(filters) => updateDetails({ ...details, filters })} />

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Date Column
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${details.date_header_uuid}`}
                    placeholder="Select a date column"
                    onChange={(value) => updateDetails({ ...details, date_header_uuid: value })}
                />
            </div>
        </Fragment>}

        <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
            Max. No. of Decimals
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <IntegerInput
                value={details.decimals ?? 2}
                onChange={(value) => updateDetails({ ...details, decimals: value === 2 ? undefined : value })}
            />
        </div>
        <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
            Prefix
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Textbox
                value={details.prefix ?? ""}
                onChange={(value) => updateDetails({ ...details, prefix: value === "" ? undefined : value })}
            />
        </div>
        <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
            Suffix
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Textbox
                value={details.suffix ?? ""}
                onChange={(value) => updateDetails({ ...details, suffix: value === "" ? undefined : value })}
            />
        </div>
    </Fragment>;
}

function WidgetPivotTagsCountDetails({ lookup_tables, details, updateDetails }: WidgetDetailsProps<IWidgetPivotTagsCountDetails>) {
    const lookup_table = lookup_tables.find(lookup_table => lookup_table.uuid === details.lookup_table_uuid);

    return <Fragment>
        <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
            Table
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Dropdown
                values={lookup_tables.map((lookup_table) => lookup_table.name)}
                ids={lookup_tables.map((lookup_table) => lookup_table.uuid)}
                selected={`${details.lookup_table_uuid}`}
                placeholder="Select a table"
                onChange={(value) => updateDetails({ ...details, lookup_table_uuid: value })}
            />
        </div>

        {lookup_table !== undefined && <Fragment>
            <WidgetFilters
                lookup_table={lookup_table}
                filters={details.filters}
                updateFilters={(filters) => updateDetails({ ...details, filters })} />

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Date Column
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${details.date_header_uuid}`}
                    placeholder="Select a date column"
                    onChange={(value) => updateDetails({ ...details, date_header_uuid: value })}
                />
            </div>

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Pivot Headers
            </label>
            {details.pivot_headers_uuids.map((pivot_headers_uuid, pivot_idx) => <Fragment key={pivot_idx}>
                {pivot_idx > 0 && <div />}
                <div className="flex flex-row w-full gap-x-2 col-span-2 items-center">
                    <Dropdown
                        values={lookup_table.headers.map((header) => header.name)}
                        ids={lookup_table.headers.map((header) => header.uuid)}
                        selected={pivot_headers_uuid}
                        onChange={(value) => updateDetails({ ...details, pivot_headers_uuids: details.pivot_headers_uuids.map((_, idx) => idx === pivot_idx ? value : _) })}
                    />
                    <IconButton
                        icon={XMarkIcon}
                        onClick={() => updateDetails({ ...details, pivot_headers_uuids: details.pivot_headers_uuids.filter((_, idx) => idx !== pivot_idx) })}
                    />
                </div>
            </Fragment>)}
            {details.pivot_headers_uuids.length === 0 && <div className="col-span-1" />}
            {details.pivot_headers_uuids.length > 0 && <div className="col-span-2" />}
            <Button icon={PlusIcon} text="Add Pivot" onClick={() => { updateDetails({ ...details, pivot_headers_uuids: [...details.pivot_headers_uuids, ""] }); }} />

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Tags Headers
            </label>
            {details.tags_headers_uuids.map((tag_headers_uuid, tag_idx) => <Fragment key={tag_idx}>
                {tag_idx > 0 && <div />}
                <div className="flex flex-row w-full gap-x-2 col-span-2">
                    <Dropdown
                        values={lookup_table.headers.map((header) => header.name)}
                        ids={lookup_table.headers.map((header) => header.uuid)}
                        selected={tag_headers_uuid}
                        onChange={(value) => updateDetails({ ...details, tags_headers_uuids: details.tags_headers_uuids.map((_, idx) => idx === tag_idx ? value : _) })}
                    />
                    <Textbox
                        value={details.tags_header_names[tag_idx]}
                        onChange={(value) => updateDetails({ ...details, tags_header_names: details.tags_header_names.map((_, idx) => idx === tag_idx ? value : _) })}
                    />
                    {details.tags_headers_uuids.length > 1 && <IconButton
                        icon={XMarkIcon}
                        onClick={() => updateDetails({ ...details, tags_headers_uuids: details.tags_headers_uuids.filter((_, idx) => idx !== tag_idx) })}
                    />}
                </div>
            </Fragment>)}
            <div className="col-span-2" />
            <Button icon={PlusIcon} text="Add Tag" onClick={() => {
                updateDetails({
                    ...details,
                    tags_headers_uuids: [...details.tags_headers_uuids, ""],
                    tags_header_names: [...details.tags_header_names, ""]
                });
            }} />
        </Fragment>}

        <div className="grid grid-cols-4 gap-4 col-span-3 pt-8 items-center">
            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                Max. Rows
            </label>
            <div className="flex flex-col w-full pr-4">
                <IntegerInput
                    value={details.top_n}
                    onChange={(value) => updateDetails({ ...details, top_n: value })}
                />
            </div>
            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                Max. Decimals
            </label>
            <div className="flex flex-col w-full pr-4">
                <IntegerInput
                    value={details.decimals ?? 2}
                    onChange={(value) => updateDetails({ ...details, decimals: value === 2 ? undefined : value })}
                />
            </div>
            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                Prefix
            </label>
            <div className="flex flex-col w-full pr-4">
                <Textbox
                    value={details.prefix ?? ""}
                    onChange={(value) => updateDetails({ ...details, prefix: value === "" ? undefined : value })}
                />
            </div>
            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                Suffix
            </label>
            <div className="flex flex-col w-full pr-4">
                <Textbox
                    value={details.suffix ?? ""}
                    onChange={(value) => updateDetails({ ...details, suffix: value === "" ? undefined : value })}
                />
            </div>
        </div>
    </Fragment>;
}

function WidgetPivotAggrListDetails({ lookup_tables, details, updateDetails }: WidgetDetailsProps<IWidgetPivotAggrListDetails>) {
    const lookup_table = lookup_tables.find(lookup_table => lookup_table.uuid === details.lookup_table_uuid);

    return <Fragment>
        <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
            Table
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Dropdown
                values={lookup_tables.map((lookup_table) => lookup_table.name)}
                ids={lookup_tables.map((lookup_table) => lookup_table.uuid)}
                selected={`${details.lookup_table_uuid}`}
                placeholder="Select a table"
                onChange={(value) => updateDetails({ ...details, lookup_table_uuid: value })}
            />
        </div>

        {lookup_table !== undefined && <Fragment>
            <WidgetFilters
                lookup_table={lookup_table}
                filters={details.filters}
                updateFilters={(filters) => updateDetails({ ...details, filters })} />

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Date Column
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${details.date_header_uuid}`}
                    placeholder="Select a date column"
                    onChange={(value) => updateDetails({ ...details, date_header_uuid: value })}
                />
            </div>

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Pivot Headers
            </label>
            {details.pivot_headers_uuids.map((pivot_headers_uuid, pivot_idx) => <Fragment key={pivot_idx}>
                {pivot_idx > 0 && <div />}
                <div className="flex flex-row w-full gap-x-2 col-span-2 items-center">
                    <Dropdown
                        values={lookup_table.headers.map((header) => header.name)}
                        ids={lookup_table.headers.map((header) => header.uuid)}
                        selected={pivot_headers_uuid}
                        onChange={(value) => updateDetails({ ...details, pivot_headers_uuids: details.pivot_headers_uuids.map((_, idx) => idx === pivot_idx ? value : _) })}
                    />
                    <IconButton
                        icon={XMarkIcon}
                        onClick={() => updateDetails({ ...details, pivot_headers_uuids: details.pivot_headers_uuids.filter((_, idx) => idx !== pivot_idx) })}
                    />
                </div>
            </Fragment>)}
            {details.pivot_headers_uuids.length === 0 && <div className="col-span-1" />}
            {details.pivot_headers_uuids.length > 0 && <div className="col-span-2" />}
            <Button icon={PlusIcon} text="Add Pivot" onClick={() => { updateDetails({ ...details, pivot_headers_uuids: [...details.pivot_headers_uuids, ""] }); }} />

            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                Aggregation Type
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={["Count rows", "Sum values"]}
                    ids={["count", "sum"]}
                    selected={details.aggregation_type}
                    onChange={(value) => updateDetails({ ...details, aggregation_type: value as "count" | "sum" })}
                />
            </div>
            {details.aggregation_type === "sum" && <Fragment>
                <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                    Value Header
                </label>
                <div className="flex flex-col w-full gap-y-2 col-span-2">
                    <Dropdown
                        values={lookup_table.headers.map((header) => header.name)}
                        ids={lookup_table.headers.map((header) => header.uuid)}
                        selected={details.value_header_uuid ?? ""}
                        onChange={(value) => updateDetails({ ...details, value_header_uuid: value })}
                    />
                </div>
            </Fragment>}

            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                Trend
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={["none", "vs. previous period"]}
                    ids={["none", "vs_last_range"]}
                    selected={details.trend}
                    onChange={(value) => updateDetails({ ...details, trend: value as "none" | "vs_last_range" })}
                />
            </div>
        </Fragment>}

        <div className="grid grid-cols-4 gap-4 col-span-3 pt-8 items-center">
            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                Max. Rows
            </label>
            <div className="flex flex-col w-full pr-4">
                <IntegerInput
                    value={details.top_n}
                    onChange={(value) => updateDetails({ ...details, top_n: value })}
                />
            </div>
            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                Max. Decimals
            </label>
            <div className="flex flex-col w-full pr-4">
                <IntegerInput
                    value={details.decimals ?? 2}
                    onChange={(value) => updateDetails({ ...details, decimals: value === 2 ? undefined : value })}
                />
            </div>
            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                Prefix
            </label>
            <div className="flex flex-col w-full pr-4">
                <Textbox
                    value={details.prefix ?? ""}
                    onChange={(value) => updateDetails({ ...details, prefix: value === "" ? undefined : value })}
                />
            </div>
            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                Suffix
            </label>
            <div className="flex flex-col w-full pr-4">
                <Textbox
                    value={details.suffix ?? ""}
                    onChange={(value) => updateDetails({ ...details, suffix: value === "" ? undefined : value })}
                />
            </div>
        </div>
    </Fragment>;
}


type WidgetFunnelChartStageProps = {
    lookup_tables: ILookupTableBase[];
    stage_idx: number;
    stage: IWidgetFunnelChartStage;
    updateStage: (stage: IWidgetFunnelChartStage) => void;
    deleteStage?: () => void;
}

function WidgetFunnelChartStage({ lookup_tables, stage_idx, stage, updateStage, deleteStage }: WidgetFunnelChartStageProps) {
    const lookup_table = lookup_tables.find(lookup_table => lookup_table.uuid === stage.lookup_table_uuid);

    return <div className="col-span-3 border border-gray-200 bg-gray-50 rounded-lg grid grid-cols-3 gap-4 p-4 items-center">
        <div className="col-span-3 flex flex-row items-center gap-x-2">
            {deleteStage !== undefined && <IconButton icon={XMarkIcon} onClick={deleteStage} size="small" />}
            <label htmlFor="title" className="text-sm font-medium leading-6 text-gray-900">
                Stage {stage_idx + 1}
            </label>
        </div>

        <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
            Name
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Textbox
                value={stage.name}
                onChange={(value) => updateStage({ ...stage, name: value })}
                placeholder="Enter stage name"
            />
        </div>
        <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
            Table
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Dropdown
                values={lookup_tables.map((lookup_table) => lookup_table.name)}
                ids={lookup_tables.map((lookup_table) => lookup_table.uuid)}
                selected={`${stage.lookup_table_uuid}`}
                placeholder="Select a table"
                onChange={(value) => updateStage({ ...stage, lookup_table_uuid: value })}
            />
        </div>

        {lookup_table !== undefined && <Fragment>
            <WidgetFilters
                lookup_table={lookup_table}
                filters={stage.filters}
                updateFilters={(filters) => updateStage({ ...stage, filters })} />
            <div className="flex flex-row items-center gap-x-2">
                <Checkbox
                    checked={stage.date_header_uuid !== undefined}
                    setChecked={(checked) => updateStage({ ...stage, date_header_uuid: checked ? "" : undefined })}
                />
                <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                    Range Date Column
                </label>
            </div>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={lookup_table.headers.map((header) => header.name)}
                    ids={lookup_table.headers.map((header) => header.uuid)}
                    selected={`${stage.date_header_uuid}`}
                    placeholder="Select a date column"
                    disabled={stage.date_header_uuid === undefined}
                    onChange={(value) => updateStage({ ...stage, date_header_uuid: value })}
                />
            </div>
            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                Aggregation Type
            </label>
            <div className="flex flex-col w-full gap-y-2 col-span-2">
                <Dropdown
                    values={["Count rows", "Sum values"]}
                    ids={["count", "sum"]}
                    selected={stage.aggregation_type}
                    onChange={(value) => updateStage({ ...stage, aggregation_type: value as "count" | "sum" })}
                />
            </div>
            {stage.aggregation_type === "sum" && <Fragment>
                <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                    Value Header
                </label>
                <div className="flex flex-col w-full gap-y-2 col-span-2">
                    <Dropdown
                        values={lookup_table.headers.map((header) => header.name)}
                        ids={lookup_table.headers.map((header) => header.uuid)}
                        selected={stage.value_header_uuid ?? ""}
                        onChange={(value) => updateStage({ ...stage, value_header_uuid: value })}
                    />
                </div>
            </Fragment>}
        </Fragment>}
    </div>;
}

function WidgetFunnelChartDetails({ lookup_tables, details, updateDetails }: WidgetDetailsProps<IWidgetFunnelChartDetails>) {
    const { stages } = details;

    return <Fragment>
        {stages.map((stage, stage_idx) => <WidgetFunnelChartStage
            key={stage_idx}
            lookup_tables={lookup_tables}
            stage_idx={stage_idx}
            stage={stage}
            updateStage={(stage) => updateDetails({ ...details, stages: details.stages.map((_, idx) => idx === stage_idx ? stage : _) })}
            deleteStage={stages.length < 3 ? undefined : () => updateDetails({ ...details, stages: details.stages.filter((_, idx) => idx !== stage_idx) })}
        />)}

        <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
            Max. No. of Decimals
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <IntegerInput
                value={details.decimals ?? 2}
                onChange={(value) => updateDetails({ ...details, decimals: value === 2 ? undefined : value })}
            />
        </div>
        <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
            Prefix
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Textbox
                value={details.prefix ?? ""}
                onChange={(value) => updateDetails({ ...details, prefix: value === "" ? undefined : value })}
            />
        </div>
        <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
            Suffix
        </label>
        <div className="flex flex-col w-full gap-y-2 col-span-2">
            <Textbox
                value={details.suffix ?? ""}
                onChange={(value) => updateDetails({ ...details, suffix: value === "" ? undefined : value })}
            />
        </div>

        {stages.length < 5 && <div className="col-span-3 flex flex-row items-center gap-x-2 justify-end">
            <Button icon={PlusIcon} text="Add Stage" onClick={() => updateDetails({ ...details, stages: [...stages, EMPTY_FUNNEL_STAGE] })} />
        </div>}
    </Fragment>;
}

type WidgetSidePanelProps = {
    open: boolean;
    all_lookup_tables: ILookupTableBase[];
    widget_uuid?: string;
    clone_widget_uuid?: string;
    onClose(widget_uuid?: string): void;
    onRemove(widget_uuid: string): void;
    onClone(widget_uuid: string): void;
}

export function WidgetSidePanel(props: WidgetSidePanelProps) {
    const { open, all_lookup_tables, widget_uuid, clone_widget_uuid, onClose, onRemove, onClone } = props;

    const memberships = useSelector(selectMemberships);
    const admin_orgs = useMemo(() => {
        // if only one org, return it (assumption is that every user has personal org and they are admin of it)
        if (memberships.length === 1) { return memberships.map((membership) => membership.org); }
        // if multiple orgs, return just the business orgs
        return memberships
            .filter((membership) => membership.role === "admin")
            .filter((membership) => membership.org.type === "business")
            .map((membership) => membership.org);
    }, [memberships]);
    const default_org_uuid = useMemo(() => admin_orgs[0]?.uuid || "", [admin_orgs]);

    const [widget_org_uuid, setWidgetOrgUuid] = useState<string>(default_org_uuid);
    const [widget_name, setWidgetName] = useState("");
    const [widget_type, setWidgetType] = useState<WidgetType>("date_count_chart");
    const [widget_details, setWidgetDetails] = useState<IWidgetDetails>(getWidgetDetails("date_count_chart"));
    const [is_saving_widget, setIsSavingWidget] = useState(false);
    const edit_type = useMemo<"new_widget" | "edit_widget">(() => widget_uuid === undefined ? "new_widget" : "edit_widget", [widget_uuid]);
    const lookup_tables = useMemo(() => all_lookup_tables.filter((lookup_table) => lookup_table.org_uuid === widget_org_uuid), [all_lookup_tables, widget_org_uuid]);

    useEffect(() => {
        const load_widget_uuid = widget_uuid ?? clone_widget_uuid;
        if (load_widget_uuid === undefined) {
            setWidgetOrgUuid(default_org_uuid);
            setWidgetName("");
            setWidgetType("date_count_chart");
            setWidgetDetails(getWidgetDetails("date_count_chart"));
        } else {
            BackendObj.extractions.getWidget({ widget_uuid: load_widget_uuid })
                .then(({ widget }) => {
                    // close if widget not found
                    if (widget === undefined) { onClose(); return; }
                    // close if user not admin of the org with the widget
                    if (admin_orgs.find(org => org.uuid === widget.org_uuid) === undefined) { onClose(); return; }
                    // set widget details
                    setWidgetOrgUuid(widget.org_uuid);
                    setWidgetName(widget.title);
                    setWidgetType(widget.type);
                    setWidgetDetails(widget.details);
                })
                .catch((error) => { onClose(); console.error(error); });
        }
    }, [widget_uuid, clone_widget_uuid, onClose, default_org_uuid, admin_orgs]);

    const updateWidgetType = (type: WidgetType) => {
        setWidgetType(type);
        setWidgetDetails(getWidgetDetails(type));
    }

    const createNewWidget = async () => {
        setIsSavingWidget(true);
        try {
            const { widget_uuid } = await BackendObj.extractions.createWidget({
                title: widget_name,
                org_uuid: widget_org_uuid,
                type: widget_type,
                details: widget_details
            });
            onClose(widget_uuid);
        } catch (error) {
            console.error(error);
        }
        setIsSavingWidget(false);
    }

    const saveEditWidget = async () => {
        if (widget_uuid === undefined) { return; }
        setIsSavingWidget(true);
        try {
            await BackendObj.extractions.updateWidget({
                widget_uuid,
                title: widget_name,
                details: widget_details
            });
            onClose(widget_uuid);
        } catch (error) {
            console.error(error);
        }
        setIsSavingWidget(false);
    }

    return <SidePanelRaw open={open} size="2xl" onClose={() => onClose()}>
        <div className="grid grid-cols-3 gap-4 px-4 py-6 items-center">
            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Title
            </label>
            <div className="col-span-2">
                <Textbox
                    value={widget_name || ""}
                    onChange={(value) => setWidgetName(value)}
                />
            </div>
            {edit_type === "new_widget" && <Fragment>
                <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    Organization
                </label>
                <div className="flex flex-row items-center gap-x-2 col-span-2">
                    {admin_orgs.map((org, idx) => <OrgPill
                        key={idx}
                        name={org.name}
                        type={org.type}
                        selected={org.uuid === widget_org_uuid}
                        onClick={() => setWidgetOrgUuid(org.uuid)}
                    />)}
                </div>
            </Fragment>}

            <label htmlFor="title" className="my-2 block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Type
            </label>
            <div className="col-span-2">
                <Dropdown
                    values={WIDGET_TYPES}
                    selected={widget_type}
                    disabled={edit_type === "edit_widget"}
                    onChange={(value) => updateWidgetType(value as WidgetType)}
                />
            </div>

            {widget_type === "date_count_chart" && <WidgetDateCountChartDetails
                lookup_tables={lookup_tables}
                details={widget_details as IWidgetDateCountChartDetails}
                updateDetails={setWidgetDetails} />}

            {widget_type === "date_distribution_chart" && <WidgetDateDistributionChartDetails
                lookup_tables={lookup_tables}
                details={widget_details as IWidgetDateDistributionChartDetails}
                updateDetails={setWidgetDetails} />}

            {widget_type === "date_ratio_chart" && <WidgetDateRatioChartDetails
                lookup_tables={lookup_tables}
                details={widget_details as IWidgetDateRatioChartDetails}
                updateDetails={setWidgetDetails} />}

            {widget_type === "date_tags_chart" && <WidgetDateTagsChartDetails
                lookup_tables={lookup_tables}
                details={widget_details as IWidgetDateTagsChartDetails}
                updateDetails={setWidgetDetails} />}

            {widget_type === "tags_chart" && <WidgetTagsChartDetails
                lookup_tables={lookup_tables}
                details={widget_details as IWidgetTagsChartDetails}
                updateDetails={setWidgetDetails} />}

            {widget_type === "date_sum_chart" && <WidgetDateSumChartDetails
                lookup_tables={lookup_tables}
                details={widget_details as IWidgetDateSumChartDetails}
                updateDetails={setWidgetDetails} />}

            {widget_type === "sum_number" && <WidgetSumNumberDetails
                lookup_tables={lookup_tables}
                details={widget_details as IWidgetSumNumberDetails}
                updateDetails={setWidgetDetails} />}

            {widget_type === "count_number" && <WidgetCountNumberDetails
                lookup_tables={lookup_tables}
                details={widget_details as IWidgetCountNumberDetails}
                updateDetails={setWidgetDetails} />}

            {widget_type === "pivot_tags_count" && <WidgetPivotTagsCountDetails
                lookup_tables={lookup_tables}
                details={widget_details as IWidgetPivotTagsCountDetails}
                updateDetails={setWidgetDetails} />}

            {widget_type === "pivot_aggr_list" && <WidgetPivotAggrListDetails
                lookup_tables={lookup_tables}
                details={widget_details as IWidgetPivotAggrListDetails}
                updateDetails={setWidgetDetails} />}

            {widget_type === "funnel_chart" && <WidgetFunnelChartDetails
                lookup_tables={lookup_tables}
                details={widget_details as IWidgetFunnelChartDetails}
                updateDetails={setWidgetDetails} />}

        </div>
        <div className="flex flex-row items-center justify-end gap-x-2 p-4">
            {widget_uuid && <Button text="Clone" onClick={() => onClone(widget_uuid)} disabled={is_saving_widget} />}
            {widget_uuid && <Button text="Delete" onClick={() => onRemove(widget_uuid)} disabled={is_saving_widget} />}
            <Button text="Cancel" onClick={() => onClose()} disabled={is_saving_widget} />
            {edit_type === "new_widget" && <Button text="Create" highlight={true} loading={is_saving_widget} disabled={is_saving_widget} onClick={createNewWidget} />}
            {edit_type === "edit_widget" && <Button text="Save" highlight={true} loading={is_saving_widget} disabled={is_saving_widget} onClick={saveEditWidget} />}
        </div>
    </SidePanelRaw>;
}